import { Flex, Icon, Text, AssistChip, type IconName, Card } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import { SlideFade } from '@upsales/components/animations';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useRef, useState } from 'react';
import AIScoring from 'App/resources/AIScoring';
import logError from 'Helpers/logError';

import './AIInsights.scss';

interface InsightProps {
	icon: IconName;
	color: 'green' | 'red' | 'yellow' | 'blue' | 'purple' | 'turcose';
	text: string;
	title: string;
}

const Insight = ({ icon, color, text, title }: InsightProps) => {
	const classes = new BemClass('AIInsights').elem('card');

	return (
		<Card borderRadius className={classes.b()}>
			<Flex space="mll mrl mtl mbl" alignItems="center" gap="u3">
				<Flex
					className={classes
						.elem('icon')
						.mod({ [color]: true })
						.b()}
				>
					<Icon name={icon} />
				</Flex>
				<Flex direction="column" gap="u1">
					<Text bold>{title}</Text>
					<Text color="grey-10">{text}</Text>
				</Flex>
			</Flex>
		</Card>
	);
};

const AIInsights = ({
	isLoading,
	insights,
	type,
	title,
	extra
}: {
	isLoading: boolean;
	insights: InsightProps[];
	type: string;
	title: string;
	extra?: string;
}) => {
	const classes = new BemClass('AIInsights');

	const [hasScored, setHasScored] = useState(false);

	const saveScoring = (score: number) => {
		setHasScored(true);
		AIScoring.save({
			type,
			score,
			extra
		}).catch(e => {
			logError(e, 'AIInsights: Failed to save scoring');
		});
	};

	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);

	return (
		<Flex className={classes.b()} space="pbl ptl pll prl" direction="column" gap={isLoading ? undefined : 'u3'}>
			<Flex gap="u3" alignItems="center">
				<AssistChip icon="stars" type="purple" title={t('account.aiFinancialAnalysis.upsalesAI')} />
				<Text bold>{isLoading ? t('account.aiFinancialAnalysis.loading') : title}</Text>
			</Flex>

			<SlideFade visible={!isLoading} speed="slow" height maxHeight={ref.current?.clientHeight}>
				<Flex direction="column" gap="u2">
					<Flex ref={ref} direction="column" gap="u2" style={{ height: '100%' }}>
						<Flex gap="u4" direction="column">
							{insights.map((insight, index) => (
								<Insight key={index} {...insight} />
							))}
						</Flex>

						<SlideFade visible={!hasScored} speed="slow">
							<Flex gap="u2">
								<Text>{t('account.aiFinancialAnalysis.wasThisInformationHelpful')}</Text>
								<Icon name="thumbs-up" onClick={() => saveScoring(10)} />
								<Icon name="thumbs-down" onClick={() => saveScoring(-10)} />
							</Flex>
						</SlideFade>
					</Flex>
				</Flex>
			</SlideFade>
		</Flex>
	);
};

export default AIInsights;
